/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEO, CloseIcon } from '../components/common';
import { BlogCard } from '../components/blog';
import { blogPosts, categories, searchPosts, getCategoryBySlug } from '../data/blogPosts';
import { colors, borderRadius, shadows, typography, transitions } from '../styles/theme';

const pageStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const headerStyles = css`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['3xl']};
    color: ${colors.text.primary};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['4xl']};
    }
  }

  p {
    color: ${colors.text.secondary};
    font-size: ${typography.fontSize.lg};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const searchWrapperStyles = css`
  max-width: 500px;
  margin: 0 auto 2rem;
  position: relative;
`;

const searchInputStyles = css`
  width: 100%;
  padding: 0.85rem 3rem 0.85rem 3rem;
  border: 2px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.sans};
  color: ${colors.text.primary};
  background: white;
  outline: none;
  transition: all ${transitions.fast};
  box-shadow: ${shadows.sm};

  &:focus {
    border-color: ${colors.primary[400]};
    box-shadow: 0 0 0 3px ${colors.primary[100]};
  }

  &::placeholder {
    color: ${colors.text.muted};
  }

  /* Tarayıcının kendi temizle butonu yerine özel buton kullanılıyor */
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
`;

const searchIconStyles = css`
  position: absolute;
  left: 1.1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.muted};
  pointer-events: none;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const clearButtonStyles = css`
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${colors.neutral[200]};
  border: none;
  border-radius: ${borderRadius.full};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${colors.text.secondary};
  transition: all ${transitions.fast};

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${colors.neutral[300]};
    color: ${colors.text.primary};
  }
`;

// Mobilde tek satırda yatay kaydırılır, geniş ekranda ortalanıp alt satıra geçer
const filtersStyles = css`
  display: flex;
  gap: 0.5rem;
  margin: 0 -1rem 2.5rem;
  padding: 0 1rem 0.25rem;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
    margin: 0 0 2.5rem;
    padding: 0;
    overflow: visible;
  }
`;

const filterButtonStyles = css`
  flex-shrink: 0;
  white-space: nowrap;
  padding: 0.5rem 1.25rem;
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  background: ${colors.background.primary};
  color: ${colors.text.secondary};
  border: 1px solid ${colors.neutral[200]};
  cursor: pointer;
  text-decoration: none;
  transition: all ${transitions.fast};

  &:hover {
    border-color: ${colors.primary[300]};
    color: ${colors.primary[700]};
  }
`;

const filterButtonActiveStyles = css`
  background: ${colors.primary[700]};
  color: white;
  border-color: ${colors.primary[700]};

  &:hover {
    background: ${colors.primary[800]};
    border-color: ${colors.primary[800]};
    color: white;
  }
`;

const gridStyles = css`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const emptyStateStyles = css`
  text-align: center;
  padding: 4rem 2rem;
  background: ${colors.background.primary};
  border: 1px dashed ${colors.neutral[300]};
  border-radius: ${borderRadius.xl};
  color: ${colors.text.secondary};

  h3 {
    font-size: ${typography.fontSize.xl};
    color: ${colors.text.primary};
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 1.5rem;
  }

  a {
    font-weight: ${typography.fontWeight.semibold};
  }
`;

export default function BlogListPage() {
  // /kategori/:slug rotasında kategori URL'den gelir, /blog'da tüm yazılar listelenir
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = slug ? getCategoryBySlug(slug) : undefined;
  const selectedCategory = activeCategory?.slug ?? null;
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = useMemo(() => {
    let posts = blogPosts;

    if (searchQuery.trim()) {
      posts = searchPosts(searchQuery.trim());
    }

    if (selectedCategory) {
      posts = posts.filter(post => post.category.slug === selectedCategory);
    }

    return posts;
  }, [selectedCategory, searchQuery]);

  return (
    <>
      <SEO
        title={activeCategory ? activeCategory.name : 'Blog'}
        description={
          activeCategory
            ? `${activeCategory.name} kategorisindeki YolSepetiGO blog yazıları.`
            : 'Çekici, yol yardım, nakliye ve vinç hizmetleri hakkında bilgilendirici blog yazıları.'
        }
        url={activeCategory ? `/kategori/${activeCategory.slug}` : '/blog'}
      />

      <div css={pageStyles}>
        <header css={headerStyles}>
          <h1>{activeCategory ? activeCategory.name : 'Blog Yazıları'}</h1>
          <p>
            {activeCategory
              ? `${activeCategory.name} hakkında yazılarımız`
              : 'Araç bakımı, yol güvenliği ve hizmetlerimiz hakkında faydalı içerikler'}
          </p>
        </header>

        <div css={searchWrapperStyles}>
          <span css={searchIconStyles}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            css={searchInputStyles}
            type="search"
            placeholder="Yazılarda ara..."
            aria-label="Yazılarda ara"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              css={clearButtonStyles}
              onClick={() => setSearchQuery('')}
              aria-label="Aramayı temizle"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        <nav css={filtersStyles} aria-label="Kategoriler">
          <Link
            to="/blog"
            css={[filterButtonStyles, !selectedCategory && filterButtonActiveStyles]}
            aria-current={!selectedCategory ? 'page' : undefined}
          >
            Tümü
          </Link>
          {categories.map(category => (
            <Link
              key={category.id}
              to={`/kategori/${category.slug}`}
              css={[
                filterButtonStyles,
                selectedCategory === category.slug && filterButtonActiveStyles,
              ]}
              aria-current={selectedCategory === category.slug ? 'page' : undefined}
            >
              {category.name}
            </Link>
          ))}
        </nav>

        {filteredPosts.length > 0 ? (
          <div css={gridStyles}>
            {filteredPosts.map(post => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div css={emptyStateStyles}>
            <h3>Yazı bulunamadı</h3>
            <p>{searchQuery ? 'Arama kriterlerinize uygun yazı bulunamadı.' : 'Bu kategoride henüz yazı bulunmuyor.'}</p>
            {searchQuery ? (
              <button css={css`color: ${colors.primary[700]}; font-weight: 600;`} onClick={() => setSearchQuery('')}>
                Aramayı temizle
              </button>
            ) : (
              <Link to="/blog">Tüm yazılara göz atın</Link>
            )}
          </div>
        )}
      </div>
    </>
  );
}
